import React, { useState, useMemo } from 'react'
import { DataTable } from 'components/DataTable'
import { CellContext, createColumnHelper } from '@tanstack/react-table'
import { OverflownTextTooltip } from 'components/OverflownTextTooltip'
import { VariantSearchResult } from 'services/search_api'
import {
  ActionsDropdown,
  createDeleteAction,
  createDownloadAction,
  createEditAction,
  createExternalAction,
  createManageFilesAction,
  createSyncAction,
} from 'components/Buttons/ActionsDropdown'
import { EditVariantModal } from 'components/Tables/VariantSearchTable/EditVariantModal'
import { SyndigoSyncModal } from 'components/Tables/VariantSearchTable/SyndigoSyncModal'
import { ManageProductFilesModal } from 'components/Tables/ProductTable/ManageProductFilesModal'
import dialogConfirm from 'components/dialogConfirm'
import {
  useDeleteVariant,
  useTriggerSyndigoSync,
  useGetSyndigoSyncStatus,
  openSyndigoSpecsheet,
  bulkDownloadSpecsheetsOptimized,
  getBulkDownloadStatus,
} from 'services/products_api'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { useDownload } from 'features/download-widget'
import { toast } from 'react-toastify'

// Common styles for text truncation
const TRUNCATION_STYLE = {
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const

interface UnifiedSearchVariantTableProps {
  data: VariantSearchResult[]
  isLoading: boolean
  currentTotalRows: number
  pageSize: number
  currentPage: number
  setCurrentPage: (page: number) => void
  onDataChange?: () => void
}

export function UnifiedSearchVariantTable({
  data,
  isLoading,
  currentTotalRows,
  pageSize,
  currentPage,
  setCurrentPage,
  onDataChange,
}: UnifiedSearchVariantTableProps) {
  const { enableDocumentLibrary } = useFeatureFlagService()

  // Edit modal state
  const [editingVariantId, setEditingVariantId] = useState<number | undefined>()

  // Sync modal state
  const [syncingVariant, setSyncingVariant] =
    useState<VariantSearchResult | null>(null)

  // Manage files modal state
  const [manageFilesVariant, setManageFilesVariant] =
    useState<VariantSearchResult | null>(null)

  // Mutations
  const deleteVariantMutation = useDeleteVariant()
  const triggerSyndigoSyncMutation = useTriggerSyndigoSync()

  // Get sync status for disabling sync button
  const { data: syndigoSyncStatus, refetch: syndigoSyncRefresh } =
    useGetSyndigoSyncStatus()

  // Bulk download hook (for spec sheet downloads)
  const { startDownload, state: downloadState } = useDownload()

  // Handlers
  const handleVariantEdit = (variant: VariantSearchResult) => {
    setEditingVariantId(variant.id)
  }

  const handleVariantDelete = async (variant: VariantSearchResult) => {
    const confirmed = await dialogConfirm(
      `Are you sure you want to delete the variant "${variant.name}"?`,
      'Delete Variant'
    )

    if (!confirmed) return

    try {
      await deleteVariantMutation.mutateAsync(variant.id)
      toast.success('Variant deleted successfully.')
      onDataChange?.()
    } catch (err) {
      console.error('Error deleting variant:', err)
      toast.error('Error deleting variant. Please try again later.')
    }
  }

  const handleVariantSync = (variant: VariantSearchResult) => {
    setSyncingVariant(variant)
  }

  const handleSyncConfirm = async () => {
    if (!syncingVariant?.id) return

    try {
      await triggerSyndigoSyncMutation.mutateAsync(syncingVariant.id)
      setSyncingVariant(null)
      onDataChange?.()
      syndigoSyncRefresh()
    } catch (err) {
      console.error('Error syncing variant:', err)
      toast.error('Error syncing variant. Please try again later.')
      setSyncingVariant(null)
    }
  }

  const handleSyncCancel = () => {
    if (!triggerSyndigoSyncMutation.isPending) {
      setSyncingVariant(null)
    }
  }

  const handleOpenSyndigo = async (variant: VariantSearchResult) => {
    if (!variant?.syndigo_product) return

    const syndigoUrl = `https://platform.syndigo.com/products/detail/${variant.syndigo_product.syndigo_product_id}`

    const link = document.createElement('a')
    link.href = syndigoUrl
    link.setAttribute('rel', 'noreferrer')
    link.setAttribute('target', '_blank')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleOpenSpecsheet = async (variant: VariantSearchResult) => {
    if (!variant?.id || !variant?.syndigo_product) return

    try {
      await openSyndigoSpecsheet(variant.id)
    } catch (err) {
      console.error('Error opening spec sheet:', err)
      toast.error('Error loading spec sheet. Please try again later.')
    }
  }

  const handleManageFiles = (variant: VariantSearchResult) => {
    setManageFilesVariant(variant)
  }

  const handleDownloadSpecsheet = async (variant: VariantSearchResult) => {
    if (!variant?.id || !variant?.syndigo_product) return

    // Only allow if not already downloading
    if (
      downloadState.status === 'downloading' ||
      downloadState.status === 'preparing' ||
      downloadState.status === 'pending'
    ) {
      toast.info('A download is already in progress')
      return
    }

    // Use download widget for individual downloads to track progress
    await startDownload({
      items: [{ id: variant.id, name: variant.name }],
      initiate: async (signal) => {
        const response = await bulkDownloadSpecsheetsOptimized(
          [variant.id],
          signal
        )
        if ('blob' in response)
          return {
            type: 'blob',
            blob: response.blob,
            filename: response.filename,
          }
        return { type: 'async', requestId: response.request_id }
      },
      getStatus: getBulkDownloadStatus,
      label: 'spec sheets',
    })
  }

  const handleEditModalClose = () => {
    setEditingVariantId(undefined)
  }

  const handleEditSuccess = () => {
    onDataChange?.()
  }

  // Create columns with actions
  const columnHelper = createColumnHelper<VariantSearchResult>()

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Variant Name',
        size: 180,
        cell: (info) => {
          const value = info.getValue() || '-'
          return (
            <div className="max-w-full">
              <OverflownTextTooltip tooltipText={value} maxLines={1}>
                <div style={TRUNCATION_STYLE}>{value}</div>
              </OverflownTextTooltip>
            </div>
          )
        },
      }),
      columnHelper.accessor('product_name', {
        header: 'Product',
        size: 150,
        cell: (info) => {
          const value = info.getValue()
          if (!value) {
            return <span className="text-gray-400">Unmatched</span>
          }
          return (
            <OverflownTextTooltip tooltipText={value} maxLines={1}>
              <div style={TRUNCATION_STYLE}>{value}</div>
            </OverflownTextTooltip>
          )
        },
      }),
      columnHelper.accessor('sku', {
        header: 'SKU',
        size: 100,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return '-'
          return (
            <OverflownTextTooltip tooltipText={value} maxLines={1}>
              <div style={TRUNCATION_STYLE}>{value}</div>
            </OverflownTextTooltip>
          )
        },
      }),
      columnHelper.accessor('format_pack_size', {
        header: 'Format / Pack Size',
        size: 110,
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('gtin', {
        header: 'GTIN / UPC',
        size: 120,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return '-'
          return (
            <OverflownTextTooltip tooltipText={value} maxLines={1}>
              <div style={TRUNCATION_STYLE}>{value}</div>
            </OverflownTextTooltip>
          )
        },
      }),
      columnHelper.accessor('avg_price', {
        header: 'Avg Price',
        size: 90,
        cell: (info) => {
          const value = info.getValue()
          if (value === null || value === undefined) return '-'
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
          }).format(value)
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        size: 80,
        meta: {
          isEditable: false,
          headerDisableOrdering: true,
          isLastColumn: true,
          rightAlign: true,
        },
        cell: ({ row }: CellContext<VariantSearchResult, unknown>) => {
          const variant = row.original
          const hasSyndigo = variant.syndigo_product !== null

          const actions = [createEditAction(() => handleVariantEdit(variant))]

          // Add Manage Files action if document library is enabled
          if (enableDocumentLibrary) {
            actions.push(
              createManageFilesAction(() => handleManageFiles(variant))
            )
          }

          if (hasSyndigo) {
            // Syndigo variant actions
            actions.push(
              createSyncAction(
                () => handleVariantSync(variant),
                syndigoSyncStatus?.status === 'in_progress'
              )
            )
            actions.push(
              createExternalAction('Open on Syndigo', () =>
                handleOpenSyndigo(variant)
              )
            )
            actions.push(
              createExternalAction('View Spec Sheet', () =>
                handleOpenSpecsheet(variant)
              )
            )
            actions.push(
              createDownloadAction('Download Spec Sheet', () =>
                handleDownloadSpecsheet(variant)
              )
            )
          } else {
            // FirstBite variant actions
            actions.push(createDeleteAction(() => handleVariantDelete(variant)))
          }

          return <ActionsDropdown actions={actions} containerWidth={180} />
        },
      }),
    ],
    [columnHelper, enableDocumentLibrary, syndigoSyncStatus]
  )

  return (
    <>
      <DataTable<VariantSearchResult>
        tableKey="unified-search-variants"
        data={data}
        columns={columns}
        loading={isLoading}
        enableRowSelection={false}
        virtualizeRows={false}
        isPaginationEnabled={true}
        height={430}
        autoHideScrollbar={false}
        paginationOptions={{
          totalRows: currentTotalRows,
          pageSize: pageSize,
          setPage: setCurrentPage,
          page: currentPage,
          isPaginationLoading: isLoading,
        }}
      />

      {/* Edit Variant Modal */}
      {editingVariantId !== undefined && (
        <EditVariantModal
          show={editingVariantId !== undefined}
          variantId={editingVariantId}
          onHide={handleEditModalClose}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Syndigo Sync Modal */}
      {syncingVariant && (
        <SyndigoSyncModal
          show={!!syncingVariant}
          variantName={syncingVariant.name}
          isLoading={triggerSyndigoSyncMutation.isPending}
          onConfirm={handleSyncConfirm}
          onCancel={handleSyncCancel}
        />
      )}

      {/* Manage Files Modal */}
      {manageFilesVariant && (
        <ManageProductFilesModal
          show={!!manageFilesVariant}
          onClose={() => setManageFilesVariant(null)}
          variantId={manageFilesVariant.id}
          variantName={manageFilesVariant.name}
          productId={manageFilesVariant.product_id}
          productName={manageFilesVariant.product_name}
        />
      )}
    </>
  )
}
